import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface AboutProps {
  isDarkMode: boolean;
}

const STATS = [
  { value: '8 yrs', label: 'On the market' },
  { value: '60+', label: 'Apps shipped' },
  { value: '24', label: 'Team members' },
  { value: '12M+', label: 'Total installs' },
];

const STEPS = [
  {
    n: '01',
    title: 'Discovery',
    desc: 'We dive into your idea — market fit, competitors, and technical feasibility. A clear brief is the foundation of every project.',
  },
  {
    n: '02',
    title: 'Strategy & roadmap',
    desc: 'Our team maps out the tech stack, development phases, marketing plan, and key milestones. You get full visibility before a single line of code.',
  },
  {
    n: '03',
    title: 'Build & iterate',
    desc: 'Design, development, QA — agile sprints with weekly demos. You see the product take shape and give feedback at every stage.',
  },
  {
    n: '04',
    title: 'Launch & scale',
    desc: 'App store submission, marketing campaigns go live, user acquisition kicks in. We stay with you post-launch to iterate and grow.',
  },
];

const About = ({ isDarkMode }: AboutProps) => {
  const sectionRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(sectionRef.current?.querySelectorAll('[data-anim]') ?? [], {
        opacity: 0,
        y: 30,
        duration: 1,
        ease: 'power3.out',
        stagger: 0.08,
        scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' },
      });
      gsap.from(sectionRef.current?.querySelectorAll('[data-stat]') ?? [], {
        opacity: 0,
        y: 30,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.08,
        scrollTrigger: { trigger: sectionRef.current, start: 'top 60%' },
      });
      gsap.from(sectionRef.current?.querySelectorAll('[data-step]') ?? [], {
        opacity: 0,
        x: -30,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.1,
        scrollTrigger: { trigger: sectionRef.current, start: 'top 50%' },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about"
      className={`relative w-full py-32 md:py-44 px-6 md:px-12 transition-colors duration-700 ${
        isDarkMode ? 'bg-[#050505] text-white' : 'bg-[#f5f5f5] text-black'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24 md:mb-32">
          <div className="lg:col-span-5">
            <div
              data-anim
              className={`mb-8 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-mono uppercase tracking-[0.2em] ${
                isDarkMode
                  ? 'border-white/20 text-white/70 bg-white/5'
                  : 'border-black/15 text-black/60 bg-black/5'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              About
            </div>
            <h2
              data-anim
              className="text-[3rem] md:text-[4.5rem] lg:text-[5rem] leading-[0.9] font-bold tracking-tighter"
            >
              A tight team building <span className="italic font-light">apps that matter</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p
              data-anim
              className={`text-lg md:text-xl leading-relaxed mb-6 ${
                isDarkMode ? 'text-white/70' : 'text-black/70'
              }`}
            >
              Solvarion LTD is an IT company specialising in utility application development — from system cleaners and optimisers to AI-powered services — along with full-cycle promotion and advertising. Based in London, we work with clients worldwide.
            </p>
            <p
              data-anim
              className={`text-base md:text-lg leading-relaxed ${
                isDarkMode ? 'text-white/55' : 'text-black/55'
              }`}
            >
              Our expertise spans mobile and desktop utility development, IT consulting, and digital marketing for apps. We help create technology products and bring them to market — from ASO and media buying to scaling user bases. Experienced project managers, developers, UI/UX designers, marketers, and QA engineers collaborate on every project.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div
          className={`grid grid-cols-2 lg:grid-cols-4 gap-px mb-24 md:mb-32 ${
            isDarkMode ? 'bg-white/10' : 'bg-black/10'
          }`}
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              data-stat
              className={`p-8 md:p-10 ${isDarkMode ? 'bg-[#050505]' : 'bg-[#f5f5f5]'}`}
            >
              <div className="text-4xl md:text-5xl font-bold tracking-tighter mb-2">{s.value}</div>
              <div
                className={`text-[11px] font-mono uppercase tracking-[0.2em] ${
                  isDarkMode ? 'text-white/45' : 'text-black/45'
                }`}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* How we work */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div
              data-anim
              className={`mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-mono uppercase tracking-[0.2em] ${
                isDarkMode
                  ? 'border-white/20 text-white/70 bg-white/5'
                  : 'border-black/15 text-black/60 bg-black/5'
              }`}
            >
              How we work
            </div>
            <h3
              data-anim
              className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-5"
            >
              From idea to app store, in four stages.
            </h3>
            <p
              data-anim
              className={`text-sm md:text-base leading-relaxed ${
                isDarkMode ? 'text-white/55' : 'text-black/55'
              }`}
            >
              We typically kick off projects within two weeks of signing. Most development cycles run in two-week sprints — you get a working build after every iteration.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div
              className={`border-t ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}
            >
              {STEPS.map((step) => (
                <div
                  key={step.n}
                  data-step
                  className={`group grid grid-cols-12 gap-6 py-8 border-b transition-colors duration-300 ${
                    isDarkMode
                      ? 'border-white/10 hover:bg-white/[0.03]'
                      : 'border-black/10 hover:bg-black/[0.02]'
                  }`}
                >
                  <div
                    className={`col-span-2 text-sm font-mono ${
                      isDarkMode ? 'text-white/40' : 'text-black/40'
                    }`}
                  >
                    {step.n}
                  </div>
                  <div className="col-span-10 md:col-span-4">
                    <h4 className="text-xl md:text-2xl font-semibold tracking-tight">
                      {step.title}
                    </h4>
                  </div>
                  <div
                    className={`col-span-12 md:col-span-6 text-sm md:text-[15px] leading-relaxed ${
                      isDarkMode ? 'text-white/55' : 'text-black/55'
                    }`}
                  >
                    {step.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA strip */}
        <div
          className={`mt-24 md:mt-32 rounded-3xl px-8 md:px-16 py-16 md:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 ${
            isDarkMode ? 'bg-white text-black' : 'bg-black text-white'
          }`}
        >
          <div className="max-w-xl">
            <h3 className="text-3xl md:text-5xl font-bold tracking-tighter leading-[0.95] mb-3">
              Start your project.
            </h3>
            <p
              className={`text-sm md:text-base ${
                isDarkMode ? 'text-black/60' : 'text-white/60'
              }`}
            >
              Ready to build? Tell us about your app idea, and we'll get back within 24 hours with a proposal.
            </p>
          </div>
          <a
            href="/contact"
            className={`rounded-full px-8 py-4 text-sm font-medium hover:scale-105 transition-transform duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              isDarkMode ? 'bg-black text-white' : 'bg-white text-black'
            }`}
          >
            Get in touch
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default About;